/**
 * Помощники блока «Открытие» в «Моих задачах»: даты, размеры, ссылки,
 * проверка вложений переписки (лимиты с сервера), длительность видео,
 * сжатие крупных фото на телефоне перед отправкой.
 */
import { plural, type TagColor } from '../ds';
import type { ThreadFileKind, ThreadLimits } from '../../lib/api/openingTasks';

// ── Даты и размеры ──────────────────────────────────────────────────

const pad = (n: number): string => String(n).padStart(2, '0');

/** «15.01.2027». Дату без времени («2027-01-15», плановое открытие) не сдвигаем поясом. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (plain) return `${plain[3]}.${plain[2]}.${plain[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** «сегодня, 10:12» / «вчера, 18:40» / «04.10, 12:05» / «04.10.2025» — в часовом поясе устройства. */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const startOf = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (days === 0) return `сегодня, ${time}`;
  if (days === 1) return `вчера, ${time}`;
  if (d.getFullYear() === now.getFullYear()) return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}, ${time}`;
  return fmtDate(iso);
}

/** «1,2 МБ» / «340 КБ». */
export function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  const mb = bytes / (1024 * 1024);
  return `${(mb < 10 ? Math.round(mb * 10) / 10 : Math.round(mb)).toString().replace('.', ',')} МБ`;
}

/** «0:48», «4:59», «1:02:10». */
export function fmtDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return '';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

/** «5 минут» для подсказок о лимите видео. */
export function fmtMinutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m >= 1 && sec % 60 === 0) return `${m} ${plural(m, 'минуты', 'минут', 'минут')}`;
  return `${sec} с`;
}

/** Инициалы для аватара: «ИП Черникова Анна» → «ЧА»; «Куратор УК» → «КУ». */
export function initials(name: string): string {
  const words = name.replace(/[«»"]/g, '').split(/\s+/).filter((w) => w && !/^(ИП|ООО|АО)$/i.test(w));
  const letters = words.slice(0, 2).map((w) => w.charAt(0).toUpperCase());
  return letters.join('') || '·';
}

// ── Ссылки ──────────────────────────────────────────────────────────

/**
 * Ссылка с бэка → маршрут вики. Бэк отдаёт короткие `/a/<slug>` и
 * `/learn/<courseId>`, а в вики статья — `/article/:slug`, курс — `/course/:id`.
 */
export function wikiLink(link: string | null | undefined): { to: string; external: boolean } | null {
  if (!link) return null;
  if (/^https?:\/\//i.test(link)) return { to: link, external: true };
  const art = /^\/a\/([^?#]+)(.*)$/.exec(link);
  if (art) return { to: `/article/${art[1]}${art[2]}`, external: false };
  const course = /^\/learn\/([^/?#]+)(.*)$/.exec(link);
  if (course) return { to: `/course/${course[1]}${course[2]}`, external: false };
  return { to: link.startsWith('/') ? link : `/${link}`, external: false };
}

// ── Стадии заявки «Осмотр помещения» (как stages.service.ts ROOM_INSPECTION) ──

export const ROOM_STAGES: Array<{ code: string; label: string; color: TagColor }> = [
  { code: 'new', label: 'Поступила', color: 'blue' },
  { code: 'curator_review', label: 'Куратор смотрит', color: 'blue' },
  // Не оранжевый: рядом с «Не подходит» он читался как красный (владелец 07.10.2026).
  { code: 'site_visit', label: 'Выезд на осмотр', color: 'indigo' },
  { code: 'approved', label: 'Помещение одобрено', color: 'green' },
  { code: 'plan_attached', label: 'Чертёж приложен', color: 'green' },
  { code: 'project_review', label: 'Проект на согласовании', color: 'gold' },
  { code: 'project_approved', label: 'Согласовано партнёром', color: 'green' },
  { code: 'tech_plan_ready', label: 'Техплан готов', color: 'green' },
];

/** Помещение убрано УК в резерв (для второй-третьей точки): не отказ и не одобрение. */
export function isRoomReserved(stageCode: string | null, status?: string | null): boolean {
  return stageCode === 'reserve' && status !== 'REJECTED';
}

export function isRoomRejected(stageCode: string | null, status?: string | null): boolean {
  return stageCode === 'rejected' || status === 'REJECTED';
}

/** Помещение ещё на проверке у куратора (до одобрения/отказа). */
export function isRoomOnReview(stageCode: string | null, status?: string | null): boolean {
  if (isRoomRejected(stageCode, status)) return false;
  if (stageCode) return stageCode === 'new' || stageCode === 'curator_review' || stageCode === 'site_visit';
  return status !== 'APPROVED' && status !== 'CONVERTED';
}

export function roomStageColor(stageCode: string | null, status?: string | null): TagColor {
  if (isRoomRejected(stageCode, status)) return 'red';
  return ROOM_STAGES.find((s) => s.code === stageCode)?.color ?? 'default';
}

export function roomStageLabel(stageCode: string | null, stageLabel: string | null, status?: string | null): string {
  // Резерв партнёру подаём спокойно: «В резерве УК», не «Не подходит».
  if (isRoomReserved(stageCode, status)) return 'В резерве УК';
  if (stageLabel) return stageLabel;
  if (isRoomRejected(stageCode, status)) return 'Не подходит';
  return ROOM_STAGES.find((s) => s.code === stageCode)?.label ?? 'Отправлено';
}

// ── Вложения переписки ──────────────────────────────────────────────

const PLAN_EXT = new Set(['pdf', 'dwg', 'dxf']);

export function fileExt(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return m ? m[1]!.toLowerCase() : '';
}

/**
 * Вид вложения — как его посчитает сервер (thread-files.ts threadFileKind):
 * image/* — фото, video/* — видео, PDF/DWG/DXF — планировка, иначе файл.
 */
export function classifyFile(file: File): ThreadFileKind {
  const mime = file.type.toLowerCase();
  if (mime.startsWith('image/')) return 'photo';
  if (mime.startsWith('video/')) return 'video';
  if (PLAN_EXT.has(fileExt(file.name)) || mime === 'application/pdf') return 'plan';
  return 'file';
}

export function limitMbFor(kind: ThreadFileKind, limits: ThreadLimits): number {
  if (kind === 'photo') return limits.maxPhotoMb;
  if (kind === 'video') return limits.maxVideoMb;
  if (kind === 'plan') return limits.maxPlanMb;
  return limits.maxFileMb;
}

export const KIND_LABEL: Record<ThreadFileKind, string> = {
  photo: 'Фото',
  video: 'Видео',
  plan: 'Планировка',
  file: 'Файл',
};

/**
 * Длительность видео через скрытый <video> (loadedmetadata). null — браузер
 * не смог прочитать (редкий кодек, iPhone/Safari без метаданных) или не успел
 * за 10 с: такое видео всё равно принимаем, длительность тогда пустая.
 *
 * Для iPhone/Safari элемент на время чтения вставляем в DOM (невидимый, 1 px):
 * без этого Safari нередко не грузит метаданные. Если браузер отдаёт
 * `duration === Infinity` (webm, потоковые файлы) — перематываем в «конец»
 * (`currentTime = 1e7`), и длительность приходит с durationchange/timeupdate.
 */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    let settled = false;
    const done = (val: number | null): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      v.onloadedmetadata = null;
      v.ondurationchange = null;
      v.ontimeupdate = null;
      v.onerror = null;
      try {
        v.pause();
        v.removeAttribute('src');
        v.load();
      } catch {
        /* элемент уже не нужен */
      }
      v.remove();
      URL.revokeObjectURL(url);
      resolve(val);
    };
    const known = (d: number): boolean => Number.isFinite(d) && d > 0;
    // Длительность появилась (в т.ч. после перемотки у «бесконечного» видео).
    const onKnown = (): void => {
      if (known(v.duration)) done(v.duration);
    };
    let seeking = false;
    const timer = window.setTimeout(() => done(null), 10_000);
    v.preload = 'metadata';
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.tabIndex = -1;
    Object.assign(v.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
    });
    v.onloadedmetadata = () => {
      if (known(v.duration)) return done(v.duration);
      if (v.duration === Infinity && !seeking) {
        seeking = true;
        try {
          v.currentTime = 1e7;
        } catch {
          done(null);
        }
        return;
      }
      if (!seeking) done(null);
    };
    v.ondurationchange = onKnown;
    v.ontimeupdate = onKnown;
    v.onerror = () => done(null);
    document.body.appendChild(v);
    v.src = url;
    v.load();
  });
}

/**
 * Крупное фото с телефона (4–12 МБ) → JPEG ≤ 2048 px, q 0.82: в переписке
 * детали помещения видны, а отправка по мобильному интернету в разы быстрее.
 * Мелкие, GIF/SVG и то, что браузер не открыл (HEIC в Chrome), — без изменений.
 */
export async function compressPhoto(file: File): Promise<File> {
  if (file.size < 1.5 * 1024 * 1024) return file;
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image'));
      el.src = url;
    });
    const maxSide = 2048;
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Файл, выбранный в композер, — уже проверен по лимитам. */
export interface PendingFile {
  key: string;
  file: File;
  kind: ThreadFileKind;
  /** Секунды видео; null — не видео или браузер не прочитал длительность (такое видео принимаем). */
  durationSec: number | null;
  /** Превью картинки (objectURL) — отзываем при удалении/отправке. */
  previewUrl: string | null;
}

/**
 * Проверка и подготовка выбранных файлов: лимит количества, размер по виду,
 * длительность видео (если браузер её прочитал). Возвращает принятые файлы и
 * понятные ошибки по отказам.
 */
export async function prepareFiles(
  picked: File[],
  alreadyCount: number,
  limits: ThreadLimits,
): Promise<{ accepted: PendingFile[]; errors: string[] }> {
  const accepted: PendingFile[] = [];
  const errors: string[] = [];
  let free = Math.max(0, limits.maxFiles - alreadyCount);
  for (const original of picked) {
    if (free <= 0) {
      errors.push(`Не больше ${limits.maxFiles} файлов в одном сообщении — «${original.name}» не добавлен.`);
      continue;
    }
    const kind = classifyFile(original);
    let file = original;
    let durationSec: number | null = null;
    if (kind === 'photo') file = await compressPhoto(original);
    const limitMb = limitMbFor(kind, limits);
    if (file.size > limitMb * 1024 * 1024) {
      errors.push(
        kind === 'video'
          ? `Видео «${original.name}» больше ${limitMb} МБ — сократите или пришлите ссылкой.`
          : `${KIND_LABEL[kind]} «${original.name}» больше ${limitMb} МБ — уменьшите файл.`,
      );
      continue;
    }
    if (kind === 'video') {
      const dur = await readVideoDuration(original);
      // Длительность не прочиталась (iPhone/Safari, редкий кодек) — не отбрасываем:
      // сервер примет такое видео с пустой длительностью, размер уже проверен выше.
      if (dur != null && dur > limits.maxVideoSec + 0.5) {
        errors.push(
          limits.maxVideoSec === 300
            ? `Видео длиннее 5 минут — сократите или пришлите ссылкой («${original.name}», ${fmtDuration(dur)}).`
            : `Видео длиннее ${fmtMinutes(limits.maxVideoSec)} — сократите или пришлите ссылкой («${original.name}», ${fmtDuration(dur)}).`,
        );
        continue;
      }
      // Сервер сравнивает с лимитом после округления: 300,4 с → 300.
      durationSec = dur != null ? Math.max(1, Math.round(dur)) : null;
    }
    accepted.push({
      key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      kind,
      durationSec,
      previewUrl: kind === 'photo' ? URL.createObjectURL(file) : null,
    });
    free -= 1;
  }
  return { accepted, errors };
}
